import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, MessageCircle, ArrowUp } from 'lucide-react';
import TuneupLogo from './TuneupLogo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    'Web Development',
    'Smart Architecture',
    'Cloud & DevOps',
    'Mobile First',
    'Fortress Security',
    'Automation & Integrations',
    'Networking & Infrastructure',
    'Digital Policy & Compliance',
    'Digital Marketing',
    'AV System Consulting',
    'IT Support & Maintenance',
  ];

  const quickLinks = [
    { name: 'Services',          href: '#services'       },
    { name: 'Products',          href: '#products'       },
    { name: 'Website Analysis',  href: '#analysis'       },
    { name: 'Founding Offer',    href: '#founding-offer' },
    { name: 'Contact',           href: '#contact'        },
  ];

  return (
    <>
      <footer className="bg-slate-900 border-t border-white/10 relative">
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

            {/* Brand */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <TuneupLogo className="w-8 h-8 shrink-0" />
                <h3 className="text-2xl font-black text-white tracking-tight uppercase">
                  TUNEUP<span className="text-orange-500">TECH</span>
                </h3>
              </div>
              <p className="text-gray-400 mb-2">
                We build fast, secure web software for Kenyan businesses.
                Honest scoping, clean code, no surprise invoices.
              </p>
              <p className="text-orange-400 text-sm italic mb-8">
                Old trust, better tools.
              </p>
              <div className="flex space-x-4 mt-2">
                <motion.a
                  href="https://wa.me/254754709099"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <MessageCircle className="w-5 h-5" />
                </motion.a>
                <motion.a
                  href="mailto:hello@tuneuptech.com"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Mail className="w-5 h-5" />
                </motion.a>
              </div>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">Services</h4>
              <ul className="space-y-2">
                {services.map((service) => (
                  <li key={service}>
                    <a href="#services" className="text-gray-400 hover:text-white transition-colors duration-200">
                      {service}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a href={link.href} className="text-gray-400 hover:text-white transition-colors duration-200">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
              <div className="space-y-3">
                <div className="flex items-center text-gray-400">
                  <Mail className="w-4 h-4 mr-3 text-blue-400" />
                  <span>hello@tuneuptech.com</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Phone className="w-4 h-4 mr-3 text-red-400" />
                  <span>+254 754 709 099</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <MapPin className="w-4 h-4 mr-3 text-orange-400" />
                  <span>Nairobi, Kenya</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom bar */}
          <motion.div
            className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="text-gray-300 text-sm mb-4 md:mb-0">
              © {currentYear} Tuneup Tech. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <a href="/privacy.html" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="/terms.html" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                Terms of Service
              </a>
            </div>
          </motion.div>
        </div>
      </footer>

      {/* Back to Top — bottom-left to avoid WhatsApp button overlap */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-6 left-6 w-10 h-10 bg-gray-800 border border-gray-700 hover:border-orange-500 hover:bg-gray-900 flex items-center justify-center text-gray-400 hover:text-orange-400 transition-all duration-200 z-40"
        style={{ borderRadius: '2px' }}
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <ArrowUp className="w-4 h-4" />
      </motion.button>
    </>
  );
};

export default Footer;
